<template>
  <Teleport to="body">
    <Transition name="cookie-banner">
      <div v-if="consentStore.showCookieBanner" class="cookie-banner">
        <div class="cookie-content">
          <div class="cookie-icon">
            <i class="fas fa-cookie-bite"></i>
          </div>

          <div class="cookie-text">
            <h3>{{ t("marco.cookies.titulo") }}</h3>
            <p>{{ t("marco.cookies.texto") }}</p>
            <router-link to="/legal/cookies" class="cookie-link">
              {{ t("marco.cookies.masInfo") }}
            </router-link>
          </div>

          <div class="cookie-actions">
            <!-- Los tres botones del sistema. Aquí vivían tres recetas propias
                 —`.btn`, `.btn-necessary`, `.btn-accept-all`— con su relleno, su
                 tipografía y su hover, heredadas del diseño anterior. -->
            <ButtonDs :label="t('marco.cookies.soloNecesarias')" variant="ghost" size="sm" @click="handleNecessaryOnly" />
            <ButtonDs :label="t('marco.cookies.configurar')" variant="ghost" size="sm" @click="handleConfigure" />
            <ButtonDs :label="t('marco.cookies.aceptarTodas')" variant="primary" size="sm" @click="handleAcceptAll" />
          </div>
        </div>

        <!-- Panel de configuración -->
        <Transition name="slide">
          <div v-if="showConfig" class="cookie-config">
            <h4>{{ t("marco.cookies.configuracion") }}</h4>

            <div class="cookie-option">
              <div class="option-info">
                <strong>{{ t("marco.cookies.tecnicas") }}</strong>
                <p>{{ t("marco.cookies.tecnicasTexto") }}</p>
              </div>
              <div class="option-toggle">
                <span class="always-active">{{ t("marco.cookies.siempreActivas") }}</span>
              </div>
            </div>

            <div class="cookie-option">
              <div class="option-info">
                <strong>{{ t("marco.cookies.analiticas") }}</strong>
                <!-- Texto del servidor, 22.09.26: dicho sin asustar y sin
                     mentir. La analítica es nuestra y vive en nuestro servidor,
                     y eso conviene decirlo porque suma confianza. -->
                <p>{{ t("marco.cookies.analiticasTexto") }}</p>
              </div>
              <div class="option-toggle">
                <label class="toggle-switch">
                  <input type="checkbox" v-model="analyticsEnabled" />
                  <span class="toggle-slider"></span>
                </label>
              </div>
            </div>

            <div class="config-actions">
              <ButtonDs :label="t('marco.cookies.cancelar')" variant="ghost" size="sm" @click="showConfig = false" />
              <ButtonDs :label="t('marco.cookies.guardar')" variant="primary" size="sm" @click="handleSaveConfig" />
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref } from "vue";
import { useConsentStore } from "@/stores/consentStore";
import ButtonDs from "@/components/ds/ButtonDs.vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const consentStore = useConsentStore();
const showConfig = ref(false);
const analyticsEnabled = ref(false);

const handleAcceptAll = () => {
  consentStore.acceptAllCookies();
  showConfig.value = false;
};

const handleNecessaryOnly = () => {
  consentStore.acceptNecessaryCookies();
  showConfig.value = false;
};

const handleConfigure = () => {
  showConfig.value = true;
  analyticsEnabled.value = false; // Por defecto desactivado
};

const handleSaveConfig = () => {
  consentStore.configureCookies(analyticsEnabled.value);
  showConfig.value = false;
};
</script>

<style lang="scss" scoped>
.cookie-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  background: var(--bg-cards);
  border-top: 2px solid var(--accent-color);
  padding: 1rem 1.5rem;

  @media (min-width: 768px) {
    padding: 1.25rem 2rem;
  }
}

.cookie-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    gap: 1.5rem;
  }
}

.cookie-icon {
  font-size: 2.5rem;
  color: var(--accent-color);
  flex-shrink: 0;

  @media (max-width: 767px) {
    text-align: center;
  }
}

.cookie-text {
  flex: 1;

  h3 {
    margin: 0 0 0.25rem 0;
    color: var(--text-color);
    font-size: 1.1rem;
    font-weight: 600;
  }

  p {
    margin: 0 0 0.5rem 0;
    color: var(--text-color);
    opacity: 0.9;
    font-size: 0.85rem;
    line-height: 1.4;
  }
}

.cookie-link {
  color: var(--accent-color);
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 500;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }
}

.cookie-actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex-shrink: 0;

  @media (min-width: 768px) {
    flex-direction: row;
    gap: 1rem;
  }
}





// Panel de configuración
.cookie-config {
  margin-top: 1.5rem;
  padding: 1.5rem;
  background: var(--bg-cards);
  border: 1px solid var(--border);
  border-radius: 0;

  h4 {
    margin: 0 0 1.5rem 0;
    color: var(--text-color);
    font-size: 1.1rem;
  }
}

.cookie-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  border-bottom: 1px solid var(--border);

  &:last-of-type {
    border-bottom: none;
  }
}

.option-info {
  flex: 1;

  strong {
    display: block;
    color: var(--text-color);
    margin-bottom: 0.25rem;
    font-size: 0.95rem;
  }

  p {
    margin: 0;
    color: var(--text-color);
    opacity: 0.7;
    font-size: 0.85rem;
  }
}

.option-toggle {
  flex-shrink: 0;
  margin-left: 1rem;
}

.always-active {
  color: var(--accent-color);
  font-size: 0.85rem;
  font-weight: 600;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 26px;

  input {
    opacity: 0;
    width: 0;
    height: 0;

    &:checked + .toggle-slider {
      background-color: var(--accent-color);
    }

    &:checked + .toggle-slider:before {
      transform: translateX(24px);
    }
  }
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--border);
  transition: 0.3s;
  border-radius: 0;

  &:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    transition: 0.3s;
    border-radius: 0;
  }
}

.config-actions {
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  justify-content: flex-end;
}



// Transiciones
.cookie-banner-enter-active,
.cookie-banner-leave-active {
  transition: transform 0.4s ease, opacity 0.4s ease;
}

.cookie-banner-enter-from,
.cookie-banner-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.slide-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>
